import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { 
  Building2, 
  Shield, 
  CheckCircle, 
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  User,
  Lock,
  Globe,
  Palette,
  Users,
  FileText,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const DepartmentRegistration = () => {
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    owner_full_name: '',
    owner_email: '',
    owner_password: '',
    department_email: '',
    department_phone: '',
    department_address: ''
  });
  const [loading, setLoading] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-generate subdomain based on department name
    if (name === 'name') {
      const subdomain = value.toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '')
        .replace(/police|department|dept/g, '')
        .substring(0, 20);
      setFormData(prev => ({ ...prev, subdomain: subdomain + 'pd' }));
      setSubdomainAvailable(null);
    }
    
    // Check subdomain availability on change
    if (name === 'subdomain') {
      setSubdomainAvailable(null);
      if (value.length >= 3) {
        checkSubdomainAvailability(value);
      }
    }
  };

  const checkSubdomainAvailability = async (subdomain) => {
    setCheckingSubdomain(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/departments/check-subdomain/${subdomain}`
      );
      setSubdomainAvailable(response.data.available);
    } catch (error) {
      console.error('Error checking subdomain:', error);
    } finally {
      setCheckingSubdomain(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/departments/register`,
        formData
      );

      toast.success('Department registered successfully!');
      
      // Store the token and user info
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Redirect to dashboard
      window.location.href = '/';
      
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <Users className="w-6 h-6 text-blue-600" />,
      title: "Multi-User Management",
      description: "Manage admins, staff, and citizen requests efficiently"
    },
    {
      icon: <FileText className="w-6 h-6 text-green-600" />,
      title: "Complete Request Tracking",
      description: "Track requests from submission to completion with full audit trail"
    },
    {
      icon: <Palette className="w-6 h-6 text-purple-600" />,
      title: "Custom Branding",
      description: "Customize colors, logo, and themes to match your department"
    },
    {
      icon: <Shield className="w-6 h-6 text-red-600" />,
      title: "Secure & Compliant",
      description: "Built with security and public records compliance in mind"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_policerecords-2/artifacts/crx2tbck_RequestTrack%20logo.png"
              alt="ReqTrak Logo"
              className="h-24 w-24 mx-auto object-contain"
            />
            <span className="text-xl font-bold text-gray-800">ReqTrak</span>
            <Badge variant="outline" className="text-xs">Request. Track. Resolve.</Badge>
          </div>
          <Button variant="outline" onClick={() => window.location.href = '/'}>
            Back to Portal
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          
          {/* Left Side - Features & Benefits */}
          <div className="space-y-8">
            <div>
              <Badge className="mb-4">Multi-Tenant SaaS Platform • 30-Day Free Trial</Badge>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Launch Your Department's Records Portal
              </h1>
              <p className="text-xl text-gray-600 mb-6">
                Join police departments across the country using ReqTrak to streamline public records requests, 
                improve transparency, and enhance citizen services. Start with a complete 30-day free trial.
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-600">50+</div>
                  <div className="text-sm text-gray-600">Departments</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">10k+</div>
                  <div className="text-sm text-gray-600">Requests Processed</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-600">99.9%</div>
                  <div className="text-sm text-gray-600">Uptime</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Registration Form */}
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Register Your Department
              </CardTitle>
              <CardDescription>
                Get started with your custom records portal in minutes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Department Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Department Information</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="name">Department Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Springfield Police Department"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="subdomain">Portal URL *</Label>
                    <div className="flex items-center">
                      <Input
                        id="subdomain"
                        name="subdomain"
                        value={formData.subdomain}
                        onChange={handleInputChange}
                        placeholder="springfieldpd"
                        className="rounded-r-none"
                        required
                      />
                      <span className="bg-gray-100 border border-l-0 px-3 py-2 text-sm text-gray-600 rounded-r">
                        .reqtrak.com
                      </span>
                    </div>
                    {checkingSubdomain && (
                      <p className="text-sm text-gray-500">Checking availability...</p>
                    )}
                    {subdomainAvailable === true && (
                      <p className="text-sm text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Available
                      </p>
                    )}
                    {subdomainAvailable === false && (
                      <p className="text-sm text-red-600">Not available</p>
                    )}
                  </div>
                </div>

                {/* Department Owner */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Department Owner</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="owner_full_name">Full Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="owner_full_name"
                        name="owner_full_name"
                        value={formData.owner_full_name}
                        onChange={handleInputChange}
                        placeholder="John Smith"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="owner_email">Email Address *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="owner_email"
                        name="owner_email"
                        type="email"
                        value={formData.owner_email}
                        onChange={handleInputChange}
                        placeholder="admin@springfieldpd.gov"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="owner_password">Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="owner_password"
                        name="owner_password"
                        type="password"
                        value={formData.owner_password}
                        onChange={handleInputChange}
                        placeholder="Secure password"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Contact Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="department_email">Department Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="department_email"
                          name="department_email"
                          type="email"
                          value={formData.department_email}
                          onChange={handleInputChange}
                          placeholder="records@dept.gov"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="department_phone">Phone Number *</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="department_phone"
                          name="department_phone"
                          value={formData.department_phone}
                          onChange={handleInputChange}
                          placeholder="(555) 123-4567"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="department_address">Department Address *</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="department_address"
                        name="department_address"
                        value={formData.department_address}
                        onChange={handleInputChange}
                        placeholder="123 Main St, Springfield, ST 12345"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                </div>

                <Alert>
                  <Star className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Free Setup!</strong> Get started immediately with full access to all features. 
                    No credit card required.
                  </AlertDescription>
                </Alert>

                <Button 
                  type="submit" 
                  className="w-full" 
                  size="lg"
                  disabled={loading || subdomainAvailable === false}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating Department...
                    </>
                  ) : (
                    <>
                      Create Department Portal
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DepartmentRegistration;